import React, {
  useCallback,
  useMemo,
  FC,
}                                         from 'react';
import styled, { Interpolation }          from 'styled-components';
import {
  Form,
  Field,
  FastField,
  useFormikContext,
  FormikConfig,
}                                         from 'formik';
import {
  ControlPoint,
  HighlightOff,
}                                         from '@material-ui/icons';
import {
  INoteValues,
  INote,
  ISalesFee,
  ISalesPersonFees,
  IAccountFormValues,
}                                         from '@typings/accountManagement';
import { ISelectOption }                  from '@typings/components';
import { EUploadTypes }                   from '@enums/common';
import { EFullFeeType }                   from '@enums/invoiceManagement';
import { SALESPERSON_DATA_BY_TYPE }       from '@modules/accountManagement/utils/suggestions';
import { PRICING_LABELS_WITHOUT_JPPOWER } from '@modules/invoiceManagement/utils/suggestions';
import { AddNote }                        from '@modules/accountManagement/components/AddNote';
import {
  Button,
  Select,
  Switch,
  Checkbox,
  TextInput,
  FileUpload,
  CurrencyInput,
}                                         from '@components/index';

interface IStyledProps {
  isSalesPerson?  : boolean;
  justifyContent? : string;
}

const filterSalespersonList = (
  salespersonList : ISelectOption[],
  feeValues       : ISalesFee[] = [],
  currentFeeIndex : number,
): ISelectOption[] => salespersonList.filter(({ value: id }) => {
  const assignedFeeIndex = feeValues.findIndex((fee) => fee.salesPersonId === id);

  return assignedFeeIndex === -1 || assignedFeeIndex === currentFeeIndex;
});

const Wrapper = styled.div`
  display         : flex;
  justify-content : space-between;
  position        : relative;
`;

const NotesWrapper = styled.div`
  position    : absolute;
  left        : 900px;
  max-width   : 750px;
  min-width   : 635px;
  margin-left : 20px;
`;

const SectionWrapper = styled.div`
  margin-bottom: 15px;
`;

const RowWrapper = styled(SectionWrapper)`&& {
  display     : flex;
  align-items : center;

  div {
    margin-block: 0 !important;
  }
}`;

const LineWrapper = styled.div<IStyledProps>`
  width           : ${({ isSalesPerson }): string => (isSalesPerson ? '620px' : '830px')};
  display         : flex;
  align-items     : center;
  margin          : ${({ isSalesPerson }): Interpolation<IStyledProps> => (!isSalesPerson && '3px 0')};
  justify-content : ${({ justifyContent }) => (justifyContent && justifyContent)};

  > span:first-child {
    flex-basis  : ${({ isSalesPerson }): Interpolation<IStyledProps> => !isSalesPerson && '300px'};
    font-size   : 16px;
    text-align  : ${({ isSalesPerson }): string => (!isSalesPerson ? 'right' : 'left')};
    white-space : nowrap;
    color       : ${({ theme: { textColor } }): string => textColor.black};

    h2 {
      font-size : 20px;
      margin    : 8px 0 10px;
    }
  }

  > div:last-child,
  > span:last-child {
    flex-basis  : ${({ isSalesPerson }): string => (isSalesPerson ? '460px' : '530px')};
    margin-left : 15px;
    white-space : nowrap;
  }

  > div:last-child {
    margin-top    : 10px;
    margin-bottom : 7px;
  }

  > span:last-child {
    font-size : 18px;
    color     : ${({ theme: { textColor } }): string => textColor.darkGray};
  }
`;

const LineDescription = styled.p`
  display         : flex;
  justify-content : center;
  position        : relative;
  left            : 15px;
  font-size       : 13px;
  color           : #5b5b63;
`;

const StateZipWrapper = styled.div<IStyledProps>`
  && {
    display         : flex;
    align-items     : center;
    justify-content : space-between;
    margin-top      : 10px;

    > span:first-child {
    }

    > span:last-child {
      flex-basis  : 200px;
      display     : flex;
      align-items : center;
      margin-top  : ${({ isSalesPerson }): Interpolation<IStyledProps> => !isSalesPerson && '-12px'};

      > span {
        font-size     : 14px;
        height        : 20px;
        padding-top   : 5px;
        padding-right : 6px;
      }
    }
  }
`;

const StatusSectionWrapper = styled(SectionWrapper)`
  ${LineWrapper} {
    width: 565px;

    > div:last-child,
    > span:last-child {
      flex-basis: 260px;
    }
  }
`;

const AchField = styled(Field)`
  input::placeholder {
    opacity   : 1;
    font-size : 28px;
    color     : #0063A5 !important;
  }

  input[value=""] {
    padding: 15.5px 14px 5px;
  }
`;

const FeeBlock = styled.div`
  width: 500px;
`;

const AddBlockButton = styled(ControlPoint)`&& {
  margin  : 15px 0 0 -150px;
  z-index : 10;
}`;

const DeleteBlockButton = styled(HighlightOff)`&& {
  margin  : 0 15px 0 -39px;
  z-index : 10;
}`;

const SalesFeeBlockWrapper = styled.div`
  display     : flex;
  align-items : center;
`;

const SalespersonBlock = styled.div`
  display      : inline-block;
  margin-right : 20px;
`;

interface ISalesPersonFeeBlock {
  currFee         : keyof ISalesPersonFees;
  salespersonList : ISelectOption[];
  isLoading       : boolean;
  index           : number;
  error           : any;
  values          : ISalesPersonFees;
  setFieldValue   : (field: string, value: any, shouldValidate?: boolean) => void;
  removeBlock     : (
    currFee       : string,
    index         : number,
    value         : ISalesPersonFees,
    setFieldValue : (field: string, value: ISalesFee[], shouldValidate?: boolean) => void,
  ) => void;
}

const SalespersonFeeBlock: FC<ISalesPersonFeeBlock> = ({
  currFee,
  salespersonList,
  isLoading,
  removeBlock,
  index,
  error,
  values,
  setFieldValue,
}) => {
  const handleChange = useCallback(
    (e) => {
      setFieldValue(`salesPersonFees.${currFee}[${index}].salesPersonId`, e.target.value);

      if (!e.target.value) {
        setFieldValue(`salesPersonFees.${currFee}[${index}].percentage`, 0);
      }
    },
    [currFee, index, setFieldValue],
  );

  const filteredSalespersons = useMemo<ISelectOption[]>(
    () => filterSalespersonList(salespersonList, values[currFee], index),
    [salespersonList, values, currFee, index],
  );

  return (
    <SalespersonBlock>
      <LineWrapper isSalesPerson>
        {!!index && (
          <DeleteBlockButton onClick={() => removeBlock(currFee, index, values, setFieldValue)} />
        )}

        <span>Salesperson:</span>
        <StateZipWrapper isSalesPerson>
          <FastField
            id        = {`salesPersonFees.${currFee}[${index}].salesPersonId`}
            name      = {`salesPersonFees.${currFee}[${index}].salesPersonId`}
            width     = "225px"
            align     = "left"
            options   = {filteredSalespersons}
            component = {Select}
            onChange  = {handleChange}
            noneText  = "Select a Salesperson"
            isLoading = {isLoading}
          />
          <span>
            <FastField
              id        = {`salesPersonFees.${currFee}[${index}].percentage`}
              name      = {`salesPersonFees.${currFee}[${index}].percentage`}
              component = {CurrencyInput}
              suffix    = "%"
              prefix    = ""
              width     = "100px"
              error     = {error}
            />
          </span>
        </StateZipWrapper>
      </LineWrapper>
    </SalespersonBlock>
  );
};

interface IAccountInfoFormProps {
  id?                   : string;
  states                : ISelectOption[];
  isNewAccount          : boolean;
  clientStatuses        : ISelectOption[];
  salespersonList       : ISelectOption[];
  isLoadingSalespersons : boolean;
  notes                 : INote[];
  onAddNewNote          : FormikConfig<INoteValues>['onSubmit'];
  handleToggleNoteModal : (note: INote | null) => void;
  addSalesPersonRow     : (
    blockName     : keyof ISalesPersonFees,
    data          : ISalesFee[],
    setFieldValue : (field: string, value: ISalesFee[], shouldValidate?: boolean) => void,
  ) => void;
  removeSalesPersonRow  : (
    currFee       : string,
    index         : number,
    value         : ISalesPersonFees,
    setFieldValue : (field: string, value: ISalesFee[], shouldValidate?: boolean) => void,
  ) => void;
}

export const AccountInfoForm: FC<IAccountInfoFormProps> = ({
  id,
  states,
  isNewAccount,
  clientStatuses,
  addSalesPersonRow,
  salespersonList,
  removeSalesPersonRow,
  isLoadingSalespersons,
  notes,
  onAddNewNote,
  handleToggleNoteModal,
}) => {
  const {
    dirty,
    values,
    errors,
    touched,
    isValid,
    isSubmitting,
    setFieldValue,
  } = useFormikContext<IAccountFormValues>();

  return (
    <Wrapper>
      <Form noValidate>
        <div>
          <SectionWrapper>
            <LineWrapper>
              <span>
                <h2>Account Information</h2>
              </span>
              <span />
            </LineWrapper>

            <LineWrapper>
              <span>Account Number:</span>
              <span>{values.cuid}</span>
            </LineWrapper>

            <LineWrapper>
              <span>*Institution Name:</span>
              <Field
                id         = "name"
                name       = "name"
                type       = "text"
                inputProps = {{ maxLength: 55 }}
                component  = {TextInput}
                autoFocus
              />
            </LineWrapper>

            <LineWrapper>
              <span>URL`s Name:</span>
              <Field
                id         = "alias"
                name       = "alias"
                type       = "text"
                inputProps = {{ maxLength: 55 }}
                component  = {TextInput}
                autoFocus
              />
            </LineWrapper>

            <LineWrapper>
              <span>*Address:</span>
              <Field
                id         = "address1"
                name       = "address1"
                type       = "text"
                inputProps = {{ maxLength: 75 }}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>Address 2:</span>
              <Field
                id         = "address2"
                name       = "address2"
                type       = "text"
                inputProps = {{ maxLength: 75 }}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>*City:</span>
              <Field
                id         = "city"
                name       = "city"
                type       = "text"
                inputProps = {{ maxLength: 55 }}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>*State:</span>
              <StateZipWrapper>
                <Field
                  id        = "state"
                  name      = "state"
                  width     = "225px"
                  align     = "right"
                  noneText  = "Select a State"
                  options   = {states}
                  component = {Select}
                />
                <span>
                  <span>Zip Code:</span>
                  <Field
                    id         = "zip"
                    name       = "zip"
                    type       = "text"
                    inputProps = {{ maxLength: 5 }}
                    component  = {TextInput}
                    stripChars = "[^0-9]"
                  />
                </span>
              </StateZipWrapper>
            </LineWrapper>
          </SectionWrapper>

          <SectionWrapper>
            <LineWrapper>
              <span>*Contact Person:</span>
              <Field
                id         = "contactName"
                name       = "contactName"
                type       = "text"
                inputProps = {{ maxLength: 55 }}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>Title:</span>
              <Field
                id         = "title"
                name       = "title"
                type       = "text"
                inputProps = {{ maxLength: 75 }}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>*Phone:</span>
              <Field
                id          = "phoneNumber"
                name        = "phoneNumber"
                type        = "tel"
                placeholder = ""
                component   = {TextInput}
                stripChars = "[^0-9\s()\-+x]"
              />
            </LineWrapper>

            <LineWrapper>
              <span>Phone Extension:</span>
              <Field
                id         = "phoneExtension"
                name       = "phoneExtension"
                component  = {TextInput}
                inputProps = {{ maxLength: 15 }}
                stripChars = "[^0-9\s]"
              />
            </LineWrapper>

            <LineWrapper>
              <span>*Email:</span>
              <Field
                id         = "email"
                name       = "email"
                type       = "email"
                inputProps = {{ maxLength: 150 }}
                component  = {TextInput}
                stripChars = "[^a-zA-Z0-9@._+\-\s]*"
                error      = {touched.email && !!errors.email}
              />
            </LineWrapper>
          </SectionWrapper>

          <SectionWrapper>
            <LineWrapper>
              <span>*Institution’s Logo:</span>
              <FileUpload
                id        = "logo"
                name      = "logo"
                color     = "default"
                label     = "Choose File"
                fileType  = {EUploadTypes.Image}
              />
            </LineWrapper>
          </SectionWrapper>

          <SectionWrapper>
            <LineWrapper>
              <span>
                <h2>Invoice Information</h2>
              </span>
              <span>
                <Field
                  id        = "useDefaultLocationForInvoice"
                  name      = "useDefaultLocationForInvoice"
                  label     = "Copy Account Information"
                  component = {Checkbox}
                />
              </span>
            </LineWrapper>

            <LineWrapper>
              <span>*Institution Name:</span>
              <Field
                id         = {values.useDefaultLocationForInvoice ? 'name' : 'invoiceLocation.locationName'}
                name       = {values.useDefaultLocationForInvoice ? 'name' : 'invoiceLocation.locationName'}
                type       = "text"
                inputProps = {{ maxLength: 55 }}
                disabled   = {values.useDefaultLocationForInvoice}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>Invoice Address:</span>
              <Field
                id         = {values.useDefaultLocationForInvoice ? 'address1' : 'invoiceLocation.address1'}
                name       = {values.useDefaultLocationForInvoice ? 'address1' : 'invoiceLocation.address1'}
                type       = "text"
                inputProps = {{ maxLength: 75 }}
                disabled   = {values.useDefaultLocationForInvoice}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>Invoice Address 2:</span>
              <Field
                id         = {values.useDefaultLocationForInvoice ? 'address2' : 'invoiceLocation.address2'}
                name       = {values.useDefaultLocationForInvoice ? 'address2' : 'invoiceLocation.address2'}
                type       = "text"
                inputProps = {{ maxLength: 75 }}
                disabled   = {values.useDefaultLocationForInvoice}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>City:</span>
              <Field
                id         = {values.useDefaultLocationForInvoice ? 'city' : 'invoiceLocation.city'}
                name       = {values.useDefaultLocationForInvoice ? 'city' : 'invoiceLocation.city'}
                type       = "text"
                inputProps = {{ maxLength: 55 }}
                disabled   = {values.useDefaultLocationForInvoice}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>State:</span>
              <StateZipWrapper>
                <Field
                  id        = {values.useDefaultLocationForInvoice ? 'state' : 'invoiceLocation.state'}
                  name      = {values.useDefaultLocationForInvoice ? 'state' : 'invoiceLocation.state'}
                  options   = {states}
                  width     = "225px"
                  align     = "right"
                  noneText  = "Select a State"
                  disabled  = {values.useDefaultLocationForInvoice}
                  component = {Select}
                />
                <span>
                  <span>Zip Code:</span>
                  <Field
                    id         = {values.useDefaultLocationForInvoice ? 'zip' : 'invoiceLocation.zip'}
                    name       = {values.useDefaultLocationForInvoice ? 'zip' : 'invoiceLocation.zip'}
                    type       = "text"
                    inputProps = {{ maxLength: 5 }}
                    disabled   = {values.useDefaultLocationForInvoice}
                    component  = {TextInput}
                    stripChars = "[^0-9]"
                  />
                </span>
              </StateZipWrapper>
            </LineWrapper>

            <LineWrapper>
              <span>*Invoice Email:</span>
              <Field
                id         = {values.useDefaultLocationForInvoice ? 'email' : 'invoiceLocation.email'}
                name       = {values.useDefaultLocationForInvoice ? 'email' : 'invoiceLocation.email'}
                type       = "text"
                inputProps = {{ maxLength: 150 }}
                disabled   = {values.useDefaultLocationForInvoice}
                component  = {TextInput}
                stripChars = "[^a-zA-Z0-9@._+\-\s]*"
                error      = {touched?.invoiceLocation?.email && !!errors?.invoiceLocation?.email}
                isInvoice
              />
            </LineWrapper>

            <LineWrapper justifyContent="end">
              <LineDescription>
                When adding more than one invoice email, separate each email address using a space.
              </LineDescription>
            </LineWrapper>

          </SectionWrapper>

          <SectionWrapper>
            <LineWrapper>
              <span>
                <h2>ACH Billing Information</h2>
              </span>
              <span />
            </LineWrapper>

            <LineWrapper>
              <span>Bank name:</span>
              <Field
                id         = "achBillingInformation.bankName"
                name       = "achBillingInformation.bankName"
                type       = "text"
                inputProps = {{ maxLength: 55 }}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>Routing Number:</span>
              <Field
                id         = "achBillingInformation.routingNumber"
                name       = "achBillingInformation.routingNumber"
                type       = "text"
                inputProps = {{ maxLength: 25 }}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>Account Number:</span>
              <Field
                id         = "achBillingInformation.accountNumber"
                name       = "achBillingInformation.accountNumber"
                type       = "text"
                inputProps = {{ maxLength: 25 }}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>Encoded Authorization Number:</span>
              <AchField
                id          = "achBillingInformation.encodedAuthorizationNumber"
                name        = "achBillingInformation.encodedAuthorizationNumber"
                type        = "password"
                placeholder = {id ? '•••••••••' : ''}
                component   = {TextInput}
              />
            </LineWrapper>
          </SectionWrapper>

          <SectionWrapper>
            <LineWrapper>
              <span>
                <h2>Pricing</h2>
              </span>
              <span />
            </LineWrapper>

            {Object.keys(values.salesPersonFees).map((key: keyof ISalesPersonFees) => {
              const { pricingKey } = SALESPERSON_DATA_BY_TYPE[key] ?? {};
              const fee            = EFullFeeType[pricingKey];

              return (
                <RowWrapper key={key}>
                  <FeeBlock>
                    <LineWrapper>
                      <span>{`${PRICING_LABELS_WITHOUT_JPPOWER[pricingKey]}:`}</span>
                      <Field
                        id        = {`pricing.${fee}.value`}
                        name      = {`pricing.${fee}.value`}
                        component = {CurrencyInput}
                        maxLength = {8}
                      />
                    </LineWrapper>
                  </FeeBlock>

                  <>
                    <span>Taxable:</span>
                    <span>
                      <Field
                        id        = {`pricing.${fee}.taxable`}
                        name      = {`pricing.${fee}.taxable`}
                        component = {Switch}
                      />
                    </span>
                  </>

                  <div>
                    <SalesFeeBlockWrapper>
                      {(values.salesPersonFees?.[key] ?? []).map((item, index) => (
                        <SalespersonFeeBlock
                          key             = {`${key}_${item.salesPersonId || item.type}`}
                          currFee         = {key}
                          salespersonList = {salespersonList}
                          isLoading       = {isLoadingSalespersons}
                          removeBlock     = {removeSalesPersonRow}
                          index           = {index}
                          error           = {errors.salesPersonFees?.[key]}
                          values          = {values.salesPersonFees}
                          setFieldValue   = {setFieldValue}
                        />
                      ))}

                      <AddBlockButton
                        onClick={() => addSalesPersonRow(
                          key,
                          values.salesPersonFees[key],
                          setFieldValue,
                        )}
                      />
                    </SalesFeeBlockWrapper>
                  </div>
                </RowWrapper>
              );
            })}
          </SectionWrapper>

          <StatusSectionWrapper>
            <LineWrapper>
              <span>
                <h2>Status</h2>
              </span>
              <span />
            </LineWrapper>

            <LineWrapper>
              <span>Status:</span>
              <Field
                id        = "status"
                name      = "status"
                options   = {clientStatuses}
                width     = "256px"
                component = {Select}
              />
            </LineWrapper>
          </StatusSectionWrapper>

          <LineWrapper>
            <span />
            <span>
              <Button
                label     = {isNewAccount ? 'Create' : 'Save'}
                type      = "submit"
                width     = "256px"
                margin    = "20px 0 0"
                isLoading = {isSubmitting}
                disabled  = {!isValid || (!isNewAccount && !dirty) || isSubmitting}
              />
            </span>
          </LineWrapper>
        </div>
      </Form>

      {!isNewAccount && (
        <NotesWrapper>
          <AddNote
            notes                 = {notes}
            onAddNewNote          = {onAddNewNote}
            handleToggleNoteModal = {handleToggleNoteModal}
          />
        </NotesWrapper>
      )}
    </Wrapper>
  );
};
